// The public privacy notice page, /privacy (Session ASQ, Stream G, Item 5). No login.
//
// It shows exactly what an admin saved in Settings, rendered as simple Markdown by the one shared renderer
// (src/components/SimpleMarkdown.tsx, which the Settings preview uses too), with "Last updated DD/MM/YYYY".
//
// UNTIL A NOTICE IS SAVED THIS PAGE DOES NOT EXIST: the server answers 404 not_published and the page shows the
// platform's ordinary not-found page, never placeholder wording. Clearing the notice in Settings takes it away again.
//
// Visual treatment: the AcceptQuote world, as the repair estimate page reuses it (light canvas, the dark logo band, a
// white card, the dark company footer bar). It imports AcceptQuote.css and adds no stylesheet and no colour of its own:
// headings take the page's own purple ink variable, links the page's own link rule.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { NotFound } from "../NotFound";
import { FullScreenLoader } from "../../components/Loader";
import { SimpleMarkdown } from "../../components/SimpleMarkdown";
import { formatNoticeDate, loadPrivacyNotice, type PrivacyNoticeState } from "../../lib/privacyNotice";
import "./AcceptQuote.css";

const PHONE = "0203 576 4760";

export function PrivacyNoticePage() {
  const [state, setState] = useState<PrivacyNoticeState | null>(null);

  useEffect(() => {
    let live = true;
    void loadPrivacyNotice().then((s) => {
      if (live) setState(s);
    });
    return () => {
      live = false;
    };
  }, []);

  if (!state) return <FullScreenLoader />;
  if (state.status === "unpublished") return <NotFound />;

  if (state.status === "error") {
    return (
      <Shell>
        <div className="sec" role="alert">
          <p className="sec-title">The privacy notice could not be loaded</p>
          <p>Please try again in a moment. If the trouble continues, call us on {PHONE}.</p>
        </div>
        <Footer />
      </Shell>
    );
  }

  const updated = formatNoticeDate(state.updatedAt);
  return (
    <Shell>
      <div className="intro">
        <h1>Privacy notice</h1>
        {updated ? <p className="lead">Last updated {updated}</p> : null}
      </div>
      <article className="sec" aria-label="Privacy notice" style={{ marginTop: 20 }}>
        <SimpleMarkdown text={state.text} headingLevel={2} headingStyle={{ color: "var(--cq-purple-text)" }} />
      </article>
      <Footer />
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="cq">
      <div className="cust-hdr">
        <div className="brand">
          <img className="brand-logo" src="/brand/ele-logo.png" alt="East London Elevators" />
          <small>Privacy notice</small>
        </div>
        <div className="hdr-contact">
          <b>{PHONE}</b>
          <br />
          info@eastlondonelevators.co.uk
          <br />
          eastlondonelevators.co.uk
        </div>
      </div>
      <div className="wrap">{children}</div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="ftr">
      <p>For and on behalf of East London Elevators.</p>
      <p className="co">East London Elevators Ltd, company 15429089, VAT GB493339461 | {PHONE} | info@eastlondonelevators.co.uk</p>
      <div className="cq-footbar cq-noprint">
        <p>East London Elevators Ltd &middot; Company No. 15429089 &middot; VAT GB493339461</p>
        <p>Unit 82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE</p>
        <p>
          <b>{PHONE}</b> &middot; <a href="mailto:info@eastlondonelevators.co.uk">info@eastlondonelevators.co.uk</a> &middot; eastlondonelevators.co.uk
        </p>
      </div>
    </footer>
  );
}
